import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Pack } from '../../models/Pack';
import { CreateLinkPackModal, DeletePackModal, LinkPackModal, PackModal, ViewPackModal } from '../modals';
import { CreateLinkPackModalRef } from '../modals/CreateLinkPackModal';
import { LinkPackModalRef } from '../modals/LinkPackModal';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithPack: (pack: Pack) => void;
}

export interface PacksModalsRef {
  openCreateModal: () => void;
  openViewModal: (pack: Pack) => void;
  openEditModal: (pack: Pack) => void;
  openDeleteModal: (pack: Pack) => void;
  openLinkModal: (pack: Pack) => void;
}

interface PacksModalsProps {
  onRefetchPacks?: () => void;
}

const PacksModals = forwardRef<PacksModalsRef, PacksModalsProps>(({ onRefetchPacks }, ref) => {
  const createModalRef = useRef<ExtendedModalCenterRef>(null);
  const editModalRef = useRef<ExtendedModalCenterRef>(null);
  const deleteModalRef = useRef<ExtendedModalCenterRef>(null);
  const viewModalRef = useRef<ExtendedModalCenterRef>(null);
  const linkModalRef = useRef<LinkPackModalRef>(null);
  const createLinkModalRef = useRef<CreateLinkPackModalRef>(null);

  const handleOpenCreateLink = (pack: Pack) => {
    createLinkModalRef.current?.openWithPack(pack, () => {
      // Callback de éxito: refrescar el listado de enlaces en la modal principal
      linkModalRef.current?.refreshPack();
      // 🎯 Refrescar la lista de packs después de crear el enlace
      onRefetchPacks?.();
    });
  };

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      createModalRef.current?.openModal();
    },
    openViewModal: (pack: Pack) => {
      viewModalRef.current?.openWithPack(pack);
    },
    openEditModal: (pack: Pack) => {
      editModalRef.current?.openWithPack(pack);
    },
    openDeleteModal: (pack: Pack) => {
      deleteModalRef.current?.openWithPack(pack);
    },
    openLinkModal: (pack: Pack) => {
      linkModalRef.current?.openWithPack(pack);
    },
  }));

  return (
    <>
      {/* 🎯 Modales - Usan patrón de estado interno para ZERO re-renders */}
      <PackModal ref={createModalRef} mode="create" />

      <PackModal ref={editModalRef} mode="edit" />

      <ViewPackModal ref={viewModalRef} />

      <DeletePackModal ref={deleteModalRef} />

      <LinkPackModal ref={linkModalRef} onCreateLink={handleOpenCreateLink} />

      <CreateLinkPackModal ref={createLinkModalRef} />
    </>
  );
});

PacksModals.displayName = 'PacksModals';

export default PacksModals;
